/*
  Defines the StealDealProducts custom element + supporting handlers.

  Loaded via <script src> from layout/theme.liquid so customElements.define
  runs in the page's initial HTML. The cart-drawer reloads the mini-cart
  section via Section Rendering API (innerHTML), which does NOT execute
  embedded <script> tags — so the class definition must live outside the
  section that gets innerHTML'd. Matches the cart-uv-nudge.js pattern.

  Asset URLs and cart currency come from window.stealDealConfig, set by an
  inline Liquid script in theme.liquid (which runs before this defer'd file).
*/
(function () {
    if (window.__stealDealInstalled) return;
    window.__stealDealInstalled = true;

    if (!customElements.get('steal-deal-products')) {
      class StealDealProducts extends HTMLElement {
        connectedCallback() {
          this.currencySymbol = this.dataset.currencySymbol || '₹';
          this._discountType = this.getDiscountType();

          this._onCartUpdated = this.handleCartUpdated.bind(this);
          this._syncFromDiscountBlock = this.syncFromDiscountBlock.bind(this);
          this._onAddToCartSubmit = this._onAddToCartSubmit.bind(this);
          this._lastCartItemCount = null;
          this._autoScrolledToSection = false;
          this._bxgxActiveCode = null;
          this._bxgxCheapestPaid = 0;
          this._pendingBxGxCode = null;
          this._productTagCache = new Map();
          this._bxgxResolveGen = 0;

          document.addEventListener('cartUpdated', this._onCartUpdated);
          document.documentElement.addEventListener('cart:updated', this._onCartUpdated);
          document.documentElement.addEventListener('cart:refresh', this._onCartUpdated);
          document.addEventListener('discount-slider:ready', this._syncFromDiscountBlock);
          this.addEventListener('submit', this._onAddToCartSubmit);

          this.initializeVisibilityFromCart();

          var self = this;
          setTimeout(function () { self.syncFromDiscountBlock(); }, 0);
        }

        disconnectedCallback() {
          document.removeEventListener('cartUpdated', this._onCartUpdated);
          document.documentElement.removeEventListener('cart:updated', this._onCartUpdated);
          document.documentElement.removeEventListener('cart:refresh', this._onCartUpdated);
          document.removeEventListener('discount-slider:ready', this._syncFromDiscountBlock);
          this.removeEventListener('submit', this._onAddToCartSubmit);
        }

        /* ── BxGx helpers (free gifts + happy-hour excluded; works with AJAX cart — not only Liquid) ── */

        /* Lowercased tags that disqualify a cart product from counting toward
           the BxGx eligible quantity. Mirrors the server-side Liquid filter
           in snippets/steal-deal.liquid. Case-insensitive match: each item's
           tags are lowercased before comparison. */
        static get EXCLUDED_TAGS() {
          return ['free-gift', 'happy-hour'];
        }

        getFreeGiftIdsFromLiquid() {
          var raw = this.getAttribute('data-free-gift-ids') || '';
          if (!raw) return [];
          return raw.split(',').map(function (id) { return parseInt(id, 10); }).filter(function (id) { return !isNaN(id); });
        }

        isFreeGiftByLineProperties(item) {
          if (!item) return false;
          var p = item.properties || {};
          return p._is_free_gift === 'true' || p._free_product === 'true';
        }

        shopRoot() {
          var r = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
          return r.slice(-1) === '/' ? r : r + '/';
        }

        productHandleFromCartUrl(url) {
          if (!url || typeof url !== 'string') return '';
          var m = url.match(/\/products\/([^/?#]+)/);
          return m ? decodeURIComponent(m[1]) : '';
        }

        buildSyncFreeGiftIdSet(cart) {
          var ids = new Set(this.getFreeGiftIdsFromLiquid());
          if (cart && Array.isArray(cart.items)) {
            cart.items.forEach(
              function (item) {
                if (this.isFreeGiftByLineProperties(item)) ids.add(item.product_id);
              }.bind(this)
            );
          }
          return ids;
        }

        resolveFreeGiftIdSetAsync(cart, syncSet) {
          var self = this;
          var root = this.shopRoot();
          var full = new Set(syncSet);
          if (!cart || !Array.isArray(cart.items)) {
            return Promise.resolve(full);
          }

          var handlesByPid = {};
          cart.items.forEach(function (item) {
            var pid = item.product_id;
            if (full.has(pid)) return;
            if (self._productTagCache.has(pid)) {
              if (self._productTagCache.get(pid)) full.add(pid);
              return;
            }
            if (!handlesByPid[pid]) {
              var h = self.productHandleFromCartUrl(item.url);
              if (h) {
                handlesByPid[pid] = h;
              } else if (!self._productTagCache.has(pid)) {
                self._productTagCache.set(pid, false);
              }
            }
          });

          var pids = Object.keys(handlesByPid);
          if (!pids.length) {
            return Promise.resolve(full);
          }

          var excludedTags = this.constructor.EXCLUDED_TAGS;
          var fetches = pids.map(function (pidStr) {
            var pid = parseInt(pidStr, 10);
            var handle = handlesByPid[pidStr] || handlesByPid[pid];
            return fetch(root + 'products/' + encodeURIComponent(handle) + '.js', {
              credentials: 'same-origin'
            })
              .then(function (r) {
                return r.ok ? r.json() : null;
              })
              .then(function (product) {
                var isExcluded = false;
                if (product && product.tags != null) {
                  var tagList =
                    typeof product.tags === 'string'
                      ? product.tags.split(',').map(function (t) {
                          return t.trim();
                        })
                      : product.tags;
                  for (var i = 0; i < tagList.length; i++) {
                    if (excludedTags.indexOf(String(tagList[i]).toLowerCase()) !== -1) {
                      isExcluded = true;
                      break;
                    }
                  }
                }
                self._productTagCache.set(pid, isExcluded);
                if (isExcluded) full.add(pid);
              })
              .catch(function () {
                self._productTagCache.set(pid, false);
              });
          });

          return Promise.all(fetches).then(function () {
            return full;
          });
        }

        getEligibleCartPidSet() {
          if (this._eligibleCartPidSet) return this._eligibleCartPidSet;
          var raw = this.getAttribute('data-eligible-cart-pids') || '';
          var ids = raw
            .split(',')
            .map(function (id) { return parseInt(id, 10); })
            .filter(function (id) { return !isNaN(id); });
          this._eligibleCartPidSet = new Set(ids);
          return this._eligibleCartPidSet;
        }

        /* data-eligible-cart-pids is rendered server-side from the cart at
           section-render time. On AJAX cart updates the attribute goes stale,
           so we re-fetch the mini-cart section and parse out the fresh value
           before computing visibility. */
        refreshEligibilityFromSection() {
          var drawer = document.querySelector('cart-drawer#mini-cart');
          var sectionId = drawer && drawer.getAttribute('section');
          if (!sectionId) return Promise.resolve();
          var self = this;
          return fetch('/?section_id=' + encodeURIComponent(sectionId), { credentials: 'same-origin' })
            .then(function (r) { return r.ok ? r.text() : null; })
            .then(function (html) {
              if (!html) return;
              var match = html.match(/data-eligible-cart-pids="([^"]*)"/);
              if (!match) return;
              var raw = match[1];
              self.setAttribute('data-eligible-cart-pids', raw);
              var ids = raw
                .split(',')
                .map(function (id) { return parseInt(id, 10); })
                .filter(function (id) { return !isNaN(id); });
              self._eligibleCartPidSet = new Set(ids);
            })
            .catch(function () {});
        }

        getEligibleItems(cart, freeGiftIdSet) {
          if (!cart || !Array.isArray(cart.items)) return [];
          var giftSet = freeGiftIdSet || this.buildSyncFreeGiftIdSet(cart);
          var eligibleSet = this.getEligibleCartPidSet();
          var hasEligibility = eligibleSet.size > 0;
          return cart.items.filter(function (item) {
            if (giftSet.has(item.product_id)) return false;
            // If no products in cart declare an offer, fall back to all
            // non-gift items (otherwise the widget would never show).
            if (!hasEligibility) return true;
            return eligibleSet.has(item.product_id);
          });
        }

        getEligibleCount(cart, freeGiftIdSet) {
          var items = this.getEligibleItems(cart, freeGiftIdSet);
          var count = 0;
          items.forEach(function (item) {
            count += item.quantity;
          });
          return count;
        }

        getDiscountType() {
          var blocks = window.discountBlocks;
          if (!blocks || !blocks.length) return 'value';
          return blocks[0].discount_type || 'value';
        }

        getItemTiers() {
          var blocks = window.discountBlocks;
          if (!blocks || !blocks.length) return [];
          return blocks
            .filter(function (b) { return b.discount_type === 'item'; })
            .map(function (b) { return { value: parseInt(b.value) || 0, code: b.discountcode, card_title: b.card_title || '' }; })
            .sort(function (a, b) { return a.value - b.value; });
        }

        updateHeading(offerLabel) {
          var headingEl = this.querySelector('.section__header .heading');
          if (!headingEl) return;
          if (!this._defaultHeading) this._defaultHeading = headingEl.textContent;
          try {
            if (offerLabel) sessionStorage.setItem('stealDealOffer', offerLabel);
            else sessionStorage.removeItem('stealDealOffer');
          } catch (e) {}
          if (offerLabel) {
            var safeLabel = String(offerLabel).replace(/[&<>"']/g, function (c) {
              return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
            });
            // iOS Safari/WebKit can't decode WebM. Feature-detect instead of
            // UA-sniffing so DevTools emulators and spoofed UAs don't false-
            // positive: ask the browser whether it can play video/webm.
            if (typeof window.__stealCanPlayWebm === 'undefined') {
              var probe = document.createElement('video');
              window.__stealCanPlayWebm = !!probe.canPlayType('video/webm');
            }
            var __sdCfg = window.stealDealConfig || {};
            var __sdDiscountIcon = __sdCfg.discountIconUrl || '';
            var __sdOfferVideo = __sdCfg.offerVideoUrl || '';
            var iconHtml = window.__stealCanPlayWebm
              ? '<video class="steal-offer-icon" width="20" height="20" autoplay loop muted playsinline preload="auto" poster="' + __sdDiscountIcon + '" aria-hidden="true">' +
                '<source src="' + __sdOfferVideo + '" type="video/webm"></video>'
              : '<img class="steal-offer-icon" src="' + __sdDiscountIcon + '" width="20" height="20" alt="" aria-hidden="true">';
            headingEl.innerHTML = '<span class="steal-offer-text">Add 1 more item to unlock ' + safeLabel +
              '</span>' + iconHtml;
            var vid = headingEl.querySelector('video.steal-offer-icon');
            if (vid) {
              var playPromise = vid.play();
              if (playPromise && typeof playPromise.catch === 'function') {
                playPromise.catch(function () { /* autoplay blocked — poster will show */ });
              }
            }
          } else {
            headingEl.textContent = this._defaultHeading;
          }
        }

        /* ── Value-tier per-product helpers ── */

        getCartSubtotalCents(cart) {
          if (!cart || !Array.isArray(cart.items)) return 0;
          var total = 0;
          cart.items.forEach(function (item) {
            total += (item.line_price || 0);
          });
          return total;
        }

        getValueTiers() {
          var blocks = (window.discountBlocks || []).filter(function (b) { return b.discount_type !== 'item'; });
          var tiers = [];
          blocks.forEach(function (b) {
            var title = (b.card_title || '').trim();
            var match = title.match(/(\d+)\s*%\s*OFF/i) || title.match(/(\d+)\s*%/);
            var pct = match ? parseInt(match[1], 10) : 0;
            if (!pct) return;
            tiers.push({
              threshold: parseInt(b.value, 10) || 0,
              percentage: pct,
              code: b.discountcode || ''
            });
          });
          return tiers.sort(function (a, b) { return a.threshold - b.threshold; });
        }

        findBestValueTier(totalRupees, tiers) {
          if (!tiers || !tiers.length) return null;
          var best = null;
          for (var i = 0; i < tiers.length; i++) {
            if (totalRupees >= tiers[i].threshold) {
              if (!best || tiers[i].percentage > best.percentage) {
                best = tiers[i];
              }
            }
          }
          return best;
        }

        _renderCardWithPercentage(card, percentage) {
          card.removeAttribute('data-steal-effective');
          card.removeAttribute('data-steal-type');

          var priceEl = card.querySelector('.steal-deal-price');
          var compareEl = card.querySelector('.steal-deal-compare');
          var percentEl = card.querySelector('.steal-deal-percentage');
          if (!priceEl) return;

          var basePrice = parseInt(priceEl.dataset.basePrice || '0', 10);
          var baseCompare = parseInt(priceEl.dataset.baseCompareAt || '0', 10);
          if (!basePrice && !baseCompare) return;

          var source = basePrice || baseCompare;
          var finalCents = source;
          if (percentage && percentage > 0) {
            finalCents = Math.round(source * (100 - percentage) / 100);
          }

          priceEl.textContent = this.formatMoney(finalCents);

          if (compareEl) {
            if (percentage > 0 && source > finalCents) {
              compareEl.textContent = this.formatMoney(source);
              compareEl.style.display = '';
            } else {
              compareEl.style.display = 'none';
            }
          }

          if (percentEl) {
            if (percentage > 0) {
              percentEl.textContent = percentage + '% OFF';
              percentEl.style.display = '';
            } else {
              percentEl.style.display = 'none';
            }
          }
        }

        updatePerCardValue(cart) {
          var tiers = this.getValueTiers();
          var subtotalCents = this.getCartSubtotalCents(cart);
          var self = this;
          var cards = this.querySelectorAll('.product-steal-item');
          this.setAttribute('data-prices-ready', '');
          try {
            sessionStorage.setItem('stealDealReady', '1');
            document.documentElement.setAttribute('data-steal-deal-ever-ready', '1');
          } catch (e) {}
          cards.forEach(function (card) {
            var priceEl = card.querySelector('.steal-deal-price');
            if (!priceEl) {
              card.setAttribute('data-best-code', '');
              card.setAttribute('data-best-percentage', '0');
              return;
            }
            var basePrice = parseInt(priceEl.dataset.basePrice || '0', 10);
            var baseCompare = parseInt(priceEl.dataset.baseCompareAt || '0', 10);
            var source = basePrice || baseCompare;
            if (!source) {
              card.setAttribute('data-best-code', '');
              card.setAttribute('data-best-percentage', '0');
              return;
            }
            var hypoRupees = (subtotalCents + source) / 100;
            var tier = self.findBestValueTier(hypoRupees, tiers);
            var pct = tier ? tier.percentage : 0;
            var code = tier ? tier.code : '';

            card.setAttribute('data-best-code', code);
            card.setAttribute('data-best-percentage', String(pct));

            self._renderCardWithPercentage(card, pct);
          });
        }

        findBxGxTier(itemCount) {
          var tiers = this.getItemTiers();
          for (var i = 0; i < tiers.length; i++) {
            if (itemCount === tiers[i].value - 1) return tiers[i];
          }
          return null;
        }

        shouldShowForBxGx(itemCount) {
          return this.findBxGxTier(itemCount) !== null;
        }

        updateCardsForBxGx(cart, freeGiftIdSetOpt) {
          if (!cart || !Array.isArray(cart.items)) return;

          var giftSet = freeGiftIdSetOpt || this.buildSyncFreeGiftIdSet(cart);
          var eligibleCount = this.getEligibleCount(cart, giftSet);
          var tier = this.findBxGxTier(eligibleCount);
          if (!tier) {
            this.updateCards(0);
            this._bxgxActiveCode = null;
            this._bxgxCheapestPaid = 0;
            this.updateHeading('');
            this.setAttribute('data-prices-ready', '');
            try {
              sessionStorage.setItem('stealDealReady', '1');
              document.documentElement.setAttribute('data-steal-deal-ever-ready', '1');
            } catch (e) {}
            return;
          }

          this._bxgxActiveCode = tier.code;
          var tierValue = tier.value;
          // For symmetric BxGx (even T): free count = T/2.
          // For asymmetric BxGy where y > x (odd T): free count = (T+1)/2.
          // Of those free, one is the recommended/added item; the rest are "free from existing":
          //   freeFromExisting = ceil(T/2) - 1
          var freeFromExisting = Math.ceil(tierValue / 2) - 1;
          this.updateHeading(tier.card_title || tier.code || '');

          var eligibleItems = this.getEligibleItems(cart, giftSet);
          var cartPrices = [];
          eligibleItems.forEach(function (item) {
            for (var q = 0; q < item.quantity; q++) {
              cartPrices.push(item.price);
            }
          });
          cartPrices.sort(function (a, b) { return a - b; });

          var paidPrices = cartPrices.slice(Math.max(freeFromExisting, 0));
          var cheapestPaid = paidPrices.length > 0 ? paidPrices[0] : 0;
          this._bxgxCheapestPaid = cheapestPaid;

          var self = this;
          var cards = this.querySelectorAll('.product-steal-item');
          cards.forEach(function (card) {
            var priceEl = card.querySelector('.steal-deal-price');
            var compareEl = card.querySelector('.steal-deal-compare');
            var percentEl = card.querySelector('.steal-deal-percentage');
            if (!priceEl) return;

            var basePrice = parseInt(priceEl.dataset.basePrice || '0', 10);
            var baseCompare = parseInt(priceEl.dataset.baseCompareAt || '0', 10);
            var source = basePrice || baseCompare;
            if (!source) return;

            var effectivePrice = source <= cheapestPaid ? 0 : source - cheapestPaid;

            card.setAttribute('data-steal-effective', String(effectivePrice));
            card.setAttribute('data-steal-type', 'bxgx');

            priceEl.textContent = self.formatMoney(effectivePrice);

            if (compareEl) {
              if (effectivePrice < source) {
                compareEl.textContent = self.formatMoney(source);
                compareEl.style.display = '';
              } else {
                compareEl.style.display = 'none';
              }
            }

            if (percentEl) {
              if (effectivePrice < source) {
                percentEl.textContent = self.formatMoney(source - effectivePrice) + ' OFF';
                percentEl.style.display = '';
              } else {
                percentEl.style.display = 'none';
              }
            }
          });

          // All card updates done — now reveal the section.
          this.setAttribute('data-prices-ready', '');
          try {
            sessionStorage.setItem('stealDealReady', '1');
            document.documentElement.setAttribute('data-steal-deal-ever-ready', '1');
          } catch (e) {}
        }

        pollForDiscountBtn(discountCode, maxWait) {
          var startTime = Date.now();
          var interval = setInterval(function () {
            var btn = document.querySelector('[data-currentcode="' + discountCode + '"]');
            if (btn && !btn.classList.contains('loader') && !btn.classList.contains('grayout')) {
              clearInterval(interval);
              btn.click();
            } else if (Date.now() - startTime > maxWait) {
              clearInterval(interval);
            }
          }, 500);
        }

        /* ── Existing methods (value-type untouched, BxGx branches added) ── */

        initializeVisibilityFromCart() {
          var self = this;
          fetch('/cart.js', { credentials: 'same-origin' })
            .then(function (response) {
              return response.ok ? response.json() : null;
            })
            .then(function (cart) {
              if (!cart || typeof cart.item_count !== 'number') return;

              if (self._discountType === 'item') {
                var syncSet = self.buildSyncFreeGiftIdSet(cart);
                self._bxgxResolveGen += 1;
                var gen = self._bxgxResolveGen;
                self.updateCardsForBxGx(cart, syncSet);
                var ecSync = self.getEligibleCount(cart, syncSet);
                self.style.display = self.shouldShowForBxGx(ecSync) ? '' : 'none';
                self.resolveFreeGiftIdSetAsync(cart, syncSet).then(function (fullSet) {
                  if (gen !== self._bxgxResolveGen) return;
                  self.updateCardsForBxGx(cart, fullSet);
                  var ec = self.getEligibleCount(cart, fullSet);
                  self.style.display = self.shouldShowForBxGx(ec) ? '' : 'none';
                });
              } else {
                self.style.display = cart.item_count === 0 ? 'none' : '';
                if (cart.item_count > 0) {
                  self.updatePerCardValue(cart);
                }
              }
              self._lastCartItemCount = cart.item_count;
            })
            .catch(function () {});
        }

        _onAddToCartSubmit(e) {
          this._pendingApplyAfterAddToCart = true;
          if (this._discountType === 'item') {
            this._pendingBxGxCode = this._bxgxActiveCode || null;
          } else {
            var card = e.target && e.target.closest ? e.target.closest('.product-steal-item') : null;
            if (card) {
              var pct = parseInt(card.getAttribute('data-best-percentage') || '0', 10);
              var code = card.getAttribute('data-best-code') || '';
              this._pendingApplyPercentage = pct > 0 ? pct : null;
              this._pendingApplyCode = code || null;
            } else {
              this._pendingApplyPercentage = null;
              this._pendingApplyCode = null;
            }
          }
        }

        syncFromDiscountBlock() {
          if (this._discountType === 'item') {
            if (this._pendingApplyAfterAddToCart && this._pendingBxGxCode) {
              var code = this._pendingBxGxCode;
              this._pendingApplyAfterAddToCart = false;
              this._pendingBxGxCode = null;
              this.pollForDiscountBtn(code, 6000);
            }
            return;
          }

          var slider = document.querySelector('discount-slider');
          if (!slider) {
            this.updateCards(0);
            this._pendingApplyAfterAddToCart = false;
            this._pendingApplyPercentage = null;
            this._pendingApplyCode = null;
            return;
          }

          var self = this;
          fetch('/cart.js', { credentials: 'same-origin' })
            .then(function (r) { return r.ok ? r.json() : null; })
            .then(function (cart) {
              if (!cart) {
                self.updateCards(0);
                return;
              }
              self.updatePerCardValue(cart);

              if (self._pendingApplyAfterAddToCart && self._pendingApplyCode) {
                var code = self._pendingApplyCode;
                self._pendingApplyAfterAddToCart = false;
                self._pendingApplyPercentage = null;
                self._pendingApplyCode = null;

                var btn = slider.querySelector('.discount-btn[data-currentcode="' + (window.CSS && CSS.escape ? CSS.escape(code) : code) + '"]');
                if (btn && !btn.classList.contains('grayout') && !btn.classList.contains('remove-discount') && !btn.classList.contains('loader')) {
                  btn.click();
                } else {
                  self.pollForDiscountBtn(code, 4000);
                }
              }
            })
            .catch(function () {});
        }

        handleCartUpdated(event) {
          let cart = null;

          if (event && event.detail && event.detail.cart) {
            cart = event.detail.cart;
          }

          const applyFromCart = (cartObject) => {
            if (!cartObject) return;
            const totalUnits = typeof cartObject.total_price === 'number' ? Math.round(cartObject.total_price / 100) : 0;
            this.setAttribute('data-cart-total', String(totalUnits));
            this.updateVisibility(cartObject);

            if (this._discountType === 'item') {
              var selfBx = this;
              var syncSet = this.buildSyncFreeGiftIdSet(cartObject);
              this._bxgxResolveGen += 1;
              var gen = this._bxgxResolveGen;
              this.updateCardsForBxGx(cartObject, syncSet);
              if (this._pendingApplyAfterAddToCart && this._pendingBxGxCode) {
                var code = this._pendingBxGxCode;
                this._pendingApplyAfterAddToCart = false;
                this._pendingBxGxCode = null;
                this.pollForDiscountBtn(code, 6000);
              }
              var ecSync = this.getEligibleCount(cartObject, syncSet);
              if (typeof cartObject.item_count === 'number') {
                this.style.display = this.shouldShowForBxGx(ecSync) ? '' : 'none';
              }
              this.resolveFreeGiftIdSetAsync(cartObject, syncSet).then(function (fullSet) {
                if (gen !== selfBx._bxgxResolveGen) return;
                selfBx.updateCardsForBxGx(cartObject, fullSet);
                if (typeof cartObject.item_count === 'number') {
                  var ec = selfBx.getEligibleCount(cartObject, fullSet);
                  selfBx.style.display = selfBx.shouldShowForBxGx(ec) ? '' : 'none';
                }
              });
            } else {
              if (!cartObject.item_count || cartObject.item_count === 0) {
                this.updateCards(0);
              } else {
                this.updatePerCardValue(cartObject);
              }
            }

            if (typeof cartObject.item_count === 'number') {
              if (this._discountType !== 'item') {
                this.style.display =
                  cartObject.item_count && cartObject.item_count > 0 ? '' : 'none';
              }

              var previousCount = this._lastCartItemCount;
              this._lastCartItemCount = cartObject.item_count;

              var isProductPage = false;
              try {
                isProductPage =
                  window.themeVariables &&
                  window.themeVariables.settings &&
                  window.themeVariables.settings.pageType === 'product';
              } catch (e) {
                isProductPage = false;
              }

              if (
                isProductPage &&
                !this._autoScrolledToSection &&
                previousCount !== null &&
                cartObject.item_count > previousCount
              ) {
                this._autoScrolledToSection = true;
                try {
                  var headerHeight = 0;
                  try {
                    var rootStyles = window.getComputedStyle(document.documentElement);
                    var headerValue = rootStyles.getPropertyValue('--header-height') || '';
                    var parsed = parseInt((headerValue.replace('px', '').trim()), 10) + 44;
                    if (!isNaN(parsed)) {
                      headerHeight = parsed;
                    }
                  } catch (e) {
                    headerHeight = 0;
                  }

                  var rect = this.getBoundingClientRect();
                  var scrollTop = window.pageYOffset || document.documentElement.scrollTop || 0;
                  var targetY = rect.top + scrollTop - headerHeight;

                  window.scrollTo({
                    top: targetY,
                    behavior: 'smooth'
                  });
                } catch (e) {}
              }
            }
          };

          var self = this;
          this.refreshEligibilityFromSection().then(function () {
            if (cart) {
              applyFromCart(cart);
              return;
            }
            fetch('/cart.js', { credentials: 'same-origin' })
              .then(function (response) {
                return response.ok ? response.json() : null;
              })
              .then(function (cartResponse) {
                if (!cartResponse) return;
                applyFromCart(cartResponse);
              })
              .catch(function () {});
          });
        }

        formatMoney(cents) {
          var value = Math.round(cents / 100);
          var formatted = value.toLocaleString(undefined, {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
          });
          return this.currencySymbol + formatted;
        }

        updateCards(percentage) {
          this._lastAppliedPercentage = percentage;
          this.setAttribute('data-prices-ready', '');
          try {
            sessionStorage.setItem('stealDealReady', '1');
            document.documentElement.setAttribute('data-steal-deal-ever-ready', '1');
          } catch (e) {}

          var cards = this.querySelectorAll('.product-steal-item');
          if (!cards.length) return;

          cards.forEach(
            function (card) {
              card.removeAttribute('data-steal-effective');
              card.removeAttribute('data-steal-type');

              var priceEl = card.querySelector('.steal-deal-price');
              var compareEl = card.querySelector('.steal-deal-compare');
              var percentEl = card.querySelector('.steal-deal-percentage');

              if (!priceEl) return;

              var basePrice = parseInt(priceEl.dataset.basePrice || '0', 10);
              var baseCompare = parseInt(priceEl.dataset.baseCompareAt || '0', 10);

              if (!basePrice && !baseCompare) return;

              var source = basePrice || baseCompare;
              var finalCents = source;

              if (percentage && percentage > 0) {
                finalCents = Math.round(source * (100 - percentage) / 100);
              }

              priceEl.textContent = this.formatMoney(finalCents);

              if (compareEl) {
                if (percentage && percentage > 0 && source > finalCents) {
                  compareEl.textContent = this.formatMoney(source);
                  compareEl.style.display = '';
                } else {
                  compareEl.style.display = 'none';
                }
              }

              if (percentEl) {
                if (percentage && percentage > 0) {
                  percentEl.textContent = percentage + '% OFF';
                  percentEl.style.display = '';
                } else {
                  percentEl.style.display = 'none';
                }
              }
            }.bind(this)
          );
        }

        updateVisibility(cart) {
          if (!cart || !Array.isArray(cart.items)) return;

          var productIdsInCart = cart.items.map(function (item) {
            return item.product_id;
          });

          var cards = this.querySelectorAll('.product-steal-item');
          if (!cards.length) return;

          cards.forEach(function (card) {
            var idAttr = card.getAttribute('data-product-id');
            var productId = idAttr ? parseInt(idAttr, 10) : NaN;
            if (!productId || isNaN(productId)) return;

            if (productIdsInCart.indexOf(productId) !== -1) {
              card.style.display = 'none';
            } else {
              card.style.display = '';
            }
          });
        }
      }

      customElements.define('steal-deal-products', StealDealProducts);
    }

    let currencySy = (window.stealDealConfig && window.stealDealConfig.currencySymbol) || '₹';

    function formatMoneyCustom(price, p) {
      let v = Math.round((price * (100 - p) / 100) / 100);
      return currencySy + v.toLocaleString(undefined, { maximumFractionDigits: 0 });
    }

    function formatBxGxMoney(effectiveCents) {
      let v = Math.round(Math.max(effectiveCents, 0) / 100);
      return currencySy + v.toLocaleString(undefined, { maximumFractionDigits: 0 });
    }

    // Image click on a steal card → open the same bottom sheet as the + button
    document.addEventListener('click', function (e) {
      var imgLink = e.target.closest && e.target.closest('.product-steal-item .product-item__aspect-ratio');
      if (!imgLink) return;
      var productItem = imgLink.closest('product-item');
      if (!productItem) return;
      var toggles = productItem.querySelectorAll('.steal-toggle');
      var visibleToggle = null;
      for (var i = 0; i < toggles.length; i++) {
        if (toggles[i].offsetParent !== null) { visibleToggle = toggles[i]; break; }
      }
      if (!visibleToggle) return;
      e.preventDefault();
      e.stopPropagation();
      visibleToggle.click();
    }, true);

    // Theme bug workaround: quick-buy-popover/-drawer fires a duplicate _load after the
    // first load completes. The duplicate setAttribute('open', '') re-triggers _load via
    // attributeChangedCallback. Even if the user closes mid-load, the queued second load
    // finishes after close and force-reopens the sheet.
    //
    // Strategy: globally patch Element.prototype.setAttribute (with a tight guard so only
    // steal-deal openables are affected) — debounce duplicate "open=''" calls within 600ms
    // of a load:end. Global patch is needed because theme.js may use the prototype method
    // directly (which bypasses instance-level overrides).
    // Theme bug workaround: theme.js's _load completes once, then re-fires itself by
    // re-asserting `open` after innerHTML is set, queuing a duplicate fetch. If the user
    // closes mid-fetch, the queued load resolves after close and the openable reopens.
    //
    // Fix: after each load:end, strip the popover/drawer's `href`. A _load triggered
    // without an href is a no-op fetch and never reaches the reopen step. The toggle
    // click handler below restores the href right before the user-initiated open so
    // genuine clicks still load fresh content.
    if (!window.__stealOpenableGuardInstalled) {
      window.__stealOpenableGuardInstalled = true;

      var __isOpenable = function (el) {
        if (!el || !el.tagName) return false;
        var t = el.tagName.toUpperCase();
        return t === 'QUICK-BUY-POPOVER' || t === 'QUICK-BUY-DRAWER';
      };

      /* Defer cart:refresh while any steal-deal openable is open. Otherwise the
         cart-drawer's innerHTML-replace destroys both the widget and the bottom
         sheet mid-interaction. Queue the events, fire them once the user closes. */
      var __stealOpenSet = new Set();
      var __stealPendingRefresh = [];
      var __isOurOpenable = function (el) {
        if (!__isOpenable(el)) return false;
        var triggers = document.querySelectorAll('.steal-toggle[aria-controls="' + el.id + '"]');
        return triggers.length > 0;
      };
      document.documentElement.addEventListener('cart:refresh', function (e) {
        if (__stealOpenSet.size === 0) return;
        // Defer cart-drawer re-render while a steal-deal bottom sheet is open
        // so its DOM isn't torn down mid-interaction.
        e.stopImmediatePropagation();
        __stealPendingRefresh.push({ detail: e.detail });
        // If the user added a product from inside this sheet, close the sheet
        // ~600ms later (lets the row's Add → "− 1 +" swap render first). The
        // close handler flushes the queued cart:refresh so the cart updates.
        __stealOpenSet.forEach(function (el) {
          if (!el.__stealAddedFromHere) return;
          setTimeout(function () {
            if (el.hasAttribute('open')) el.removeAttribute('open');
          }, 600);
        });
      }, true);

      // Track add-to-cart-like clicks INSIDE a steal-deal sheet (ATC buttons
      // OR the qty selector's "+" button on already-in-cart rows). Marking the
      // sheet here makes cart:refresh trigger the 600ms auto-close. Clicks on
      // qty buttons outside any sheet (e.g. .pilgrim-card__qty--select-variant
      // on the outer card) are ignored — they open the sheet, not add.
      document.addEventListener('click', function (e) {
        var t = e.target;
        if (!t || !t.closest) return;
        var openable = t.closest('quick-buy-popover, quick-buy-drawer');
        if (!openable || !__stealOpenSet.has(openable)) return;

        var isAtc = t.closest(
          '.size-variant-sheet__atc, .size-variant-sheet__add-btn, ' +
          '.shade-variant-sheet__atc, [data-product-add-to-cart-button]'
        );
        var isIncrease = t.closest('.custom-quantity-selector__button[data-action="increase"]');
        if (!isAtc && !isIncrease) return;

        // Shade-variant-sheet footer qty: its data-variant-id is set
        // dynamically on swatch tap. Force-sync from the currently-checked
        // radio so +/- can't target the wrong variant if anything drifted.
        if (isIncrease) {
          var qtySel = isIncrease.closest('.shade-variant-sheet__qty');
          if (qtySel) {
            var checked = openable.querySelector('.shade-variant-sheet__swatch-wrap input[type="radio"]:checked');
            if (checked && checked.value &&
                qtySel.getAttribute('data-variant-id') !== String(checked.value)) {
              qtySel.setAttribute('data-variant-id', checked.value);
            }
          }
        }

        openable.__stealAddedFromHere = true;
      }, true);
      function __flushPendingRefresh() {
        if (__stealOpenSet.size > 0) return;
        if (__stealPendingRefresh.length === 0) return;
        var last = __stealPendingRefresh.pop();
        __stealPendingRefresh.length = 0;
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', last));
      }

      function __guardOpenable(el) {
        if (!el || el.__stealGuardInstalled) return;
        el.__stealGuardInstalled = true;
        // Cache the server-rendered href once.
        var initialHref = el.getAttribute('href');
        if (initialHref) el.__stealOriginalHref = initialHref;

        // Theme bug workaround: theme.js's _load completes once, then re-fires
        // itself, re-fetching the popover and wiping the user's swatch/variant
        // selection. After each load:end, strip href so the duplicate _load is
        // a no-op. The toggle click handler below restores it before the next
        // genuine user-initiated open.
        el.addEventListener('openable-element:load:end', function () {
          var href = this.getAttribute('href');
          if (href) {
            this.__stealOriginalHref = href;
            this.removeAttribute('href');
          }
        }, true);

        el.addEventListener('openable-element:open', function () {
          if (__isOurOpenable(this)) __stealOpenSet.add(this);
        }, true);

        el.addEventListener('openable-element:close', function () {
          __stealOpenSet.delete(this);
          this.__stealAddedFromHere = false;
          setTimeout(__flushPendingRefresh, 50);
        }, true);
      }

      function __scanOpenables() {
        document.querySelectorAll('quick-buy-popover, quick-buy-drawer').forEach(__guardOpenable);
      }
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', __scanOpenables);
      } else {
        __scanOpenables();
      }
      var addObs = new MutationObserver(function (mutations) {
        for (var i = 0; i < mutations.length; i++) {
          var m = mutations[i];
          for (var j = 0; j < m.addedNodes.length; j++) {
            var n = m.addedNodes[j];
            if (n.nodeType !== 1) continue;
            if (__isOpenable(n)) __guardOpenable(n);
            if (n.querySelectorAll) {
              n.querySelectorAll('quick-buy-popover, quick-buy-drawer').forEach(__guardOpenable);
            }
          }
        }
      });
      addObs.observe(document.documentElement, { childList: true, subtree: true });

      // When the user clicks a steal-toggle, restore the openable's href before
      // theme.js fires _load — capture phase so we run before theme.js's button
      // click handler.
      document.addEventListener('click', function (e) {
        var btn = e.target && e.target.closest && e.target.closest('.steal-toggle');
        if (!btn) return;
        var controls = btn.getAttribute('aria-controls');
        if (!controls) return;
        var modal = document.getElementById(controls);
        if (modal && modal.__stealOriginalHref && !modal.getAttribute('href')) {
          modal.setAttribute('href', modal.__stealOriginalHref);
        }
      }, true);
    }

    // Install the load:end → price-rewrite listener once per modal. Each steal-toggle
    // click stores the latest percentage/mode on the modal as data, and the listener
    // reads those at runtime. Prevents listener accumulation that compounds the discount
    // on every subsequent open.
    function __ensureStealModalListener(modal) {
      if (modal.__stealUpdateInstalled) return;
      modal.__stealUpdateInstalled = true;

      modal.addEventListener("openable-element:load:end", ev => {
        const update = () => {
          const p = modal.__stealP || 0;
          const isBxGx = !!modal.__stealIsBxGx;
          // Prefer the cheapestPaid that was stashed on this modal at click time
          // (correct steal-deal-products instance). Fall back to global query.
          const cheapestPaid = (typeof modal.__stealCheapestPaid === 'number')
            ? modal.__stealCheapestPaid
            : (document.querySelector('steal-deal-products')?._bxgxCheapestPaid || 0);
          if (!isBxGx && p <= 0) return;

          // Helpers to convert cents → discounted display string, and read cents from a money text
          const readCents = (text) => {
            if (!text) return 0;
            const parts = String(text).split(currencySy);
            const last = parts[parts.length - 1] || '';
            const num = Number(last.replace(',', '').replace(/[^\d.]/g, ''));
            return isFinite(num) ? num * 100 : 0;
          };
          const applyDiscount = (cents) => {
            if (isBxGx) {
              return formatBxGxMoney(cents <= cheapestPaid ? 0 : cents - cheapestPaid);
            }
            return formatMoneyCustom(cents, p);
          };

          // Main price block(s) — NPC quick-buy renders multiple .price-list nodes
          let priceLists = ev.target.querySelectorAll('.price-list');
          priceLists.forEach(pl => {
            let priceEl = pl.querySelector('.price:not(.price--compare):not([class*="--compare"])') || pl.querySelector('.price');
            if (!priceEl) return;
            let priceText = priceEl.textContent.split(currencySy);
            let price = readCents(priceEl.textContent);
            if (price <= 0) return;
            let originalPriceText = priceText[priceText.length - 1].trim();
            pl.innerHTML =
              `<span class="price price--highlight">${applyDiscount(price)}</span>
              <span class="price price--compare">${currencySy}${originalPriceText}</span>`;
          });

          // NPC size-variant bottom sheet — one row per variant
          ev.target.querySelectorAll('.size-variant-sheet__price-row').forEach(row => {
            const priceEl = row.querySelector('.size-variant-sheet__price');
            if (!priceEl) return;
            const original = priceEl.textContent.trim();
            const cents = readCents(original);
            if (cents <= 0) return;

            priceEl.textContent = applyDiscount(cents);

            let compareEl = row.querySelector('.size-variant-sheet__compare-price');
            if (!compareEl) {
              compareEl = document.createElement('span');
              compareEl.className = 'size-variant-sheet__compare-price';
              priceEl.parentNode.insertBefore(compareEl, priceEl.nextSibling);
            }
            compareEl.textContent = original;
            compareEl.style.display = '';
          });

          // NPC size-variant per-unit (per-ml / per-gm) — in cart steal-deal we show
          // compare-at-price ÷ size (NOT the discounted value). data-perml is the
          // server-rendered variant.price ÷ size, which equals compare_at_price ÷
          // size when no separate sale price exists. We keep that value as-is.
          ev.target.querySelectorAll('.size-variant-sheet__per-unit').forEach(unitEl => {
            const original = unitEl.textContent.trim();
            const permlAttr = parseFloat(unitEl.getAttribute('data-perml') || '');
            if (!isFinite(permlAttr) || permlAttr <= 0) return;
            const suffixMatch = original.match(/\/.+$/);
            const suffix = suffixMatch ? suffixMatch[0] : '/ml';
            unitEl.textContent = currencySy + permlAttr.toFixed(1) + suffix;
          });

          // NPC shade-variant bottom sheet — footer price + swatch data-attrs
          ev.target.querySelectorAll('.shade-variant-sheet__price').forEach(priceEl => {
            const original = priceEl.textContent.trim();
            const cents = readCents(original);
            if (cents <= 0) return;
            priceEl.textContent = applyDiscount(cents);
            const compareEl = priceEl.parentElement && priceEl.parentElement.querySelector('.shade-variant-sheet__compare-price');
            if (compareEl) {
              compareEl.textContent = original;
              compareEl.style.display = '';
            }
          });
          ev.target.querySelectorAll('.shade-variant-sheet__swatch-wrap[data-price]').forEach(sw => {
            const original = sw.getAttribute('data-price') || '';
            const cents = readCents(original);
            if (cents <= 0) return;
            sw.setAttribute('data-original-price', original);
            sw.setAttribute('data-price', applyDiscount(cents));
            if (!sw.getAttribute('data-compare-price')) {
              sw.setAttribute('data-compare-price', original);
            }
          });

          ev.target.querySelectorAll('.product_form_variant-price').forEach(item => {
            // If we previously rewrote this item, the original lives inside our
            // compare span — read it from there. Otherwise the current textContent
            // is the server-fresh original. Either way, strip our compare before
            // recomputing so we never chain "₹0 ₹0 ₹595".
            const existingCompare = item.querySelector('.product_form_variant-compare-price');
            let original = existingCompare
              ? existingCompare.textContent.trim()
              : item.textContent.split('\n')[0].trim();
            if (existingCompare) existingCompare.remove();

            let originalParts = original.split(currencySy);
            let originalLast = originalParts[originalParts.length - 1];
            let itemprice = Number(originalLast.replace(',', '')) * 100;
            let discounted;

            if (isBxGx) {
              let itemEff = itemprice <= cheapestPaid ? 0 : itemprice - cheapestPaid;
              discounted = formatBxGxMoney(itemEff);
            } else {
              discounted = formatMoneyCustom(itemprice, p);
            }

            item.innerHTML = `${discounted} <span class="product_form_variant-compare-price">${original}</span>`;

            let ml = item.closest('.product_form_variant-details')?.querySelector('[data-ml]');
            if (ml) {
              let mlv = Number(ml.dataset.ml);
              if (isBxGx) {
                let itemEff = itemprice <= cheapestPaid ? 0 : itemprice - cheapestPaid;
                ml.innerHTML = `${currencySy} ${(Math.round(itemEff / 100) / mlv).toFixed(1)}/ml`;
              } else {
                ml.innerHTML = `${currencySy} ${(Number(discounted.replace(currencySy, '').replace(',', '')) / mlv).toFixed(1)}/ml`;
              }
            }

            let savings = item.closest('.product_form_variant-details')?.querySelector('.product_form_variant-savings');
            if (savings) savings.style.display = 'none';
          });
        };

        update();

        // Re-apply discount when the variant form re-renders (variant switch). Attach
        // only once per form to avoid the same listener-accumulation bug.
        var rerender = ev.target.querySelector('product-rerender');
        if (rerender) {
          var form = document.forms[rerender.getAttribute('observe-form')];
          if (form && !form.__stealRerenderInstalled) {
            form.__stealRerenderInstalled = true;
            form.addEventListener('product:rerender', update);
          }
        }
      });
    }

    document.addEventListener('click', e => {
      if (!e.target.classList.contains('steal-toggle')) return;

      let product = e.target.closest('product-item');
      if (!product) return;

      let isBxGx = product.getAttribute('data-steal-type') === 'bxgx';
      // Read per-card percentage from data attr (set by updatePerCardValue) — falls back to text
      let p = 0;
      if (!isBxGx) {
        let dataPct = parseInt(product.getAttribute('data-best-percentage') || '', 10);
        if (!isNaN(dataPct) && dataPct > 0) {
          p = dataPct;
        } else {
          let discount = product.querySelector('.discount-percentage');
          if (!discount) return;
          let parsed = Number(discount.textContent.split('%')[0]);
          p = isNaN(parsed) ? 0 : parsed;
        }
        if (p <= 0) return;
      }

      let modal = document.querySelector(`#${e.target.getAttribute("aria-controls")}`);
      if (!modal) return;

      // Use the steal-deal-products instance that contains this clicked toggle.
      // Multiple instances can co-exist (e.g. PDP steal-deal + cart steal-deal),
      // and document.querySelector would return the wrong one.
      let owningSection = product.closest('steal-deal-products');
      let cheapestPaid = owningSection ? (owningSection._bxgxCheapestPaid || 0) : 0;

      // Stash latest values on the modal — the load:end listener reads these.
      modal.__stealP = p;
      modal.__stealIsBxGx = isBxGx;
      modal.__stealCheapestPaid = cheapestPaid;

      __ensureStealModalListener(modal);
    });
  })();
